import { exists, isPartialDate, nowPartialDate, subtractMonths } from 'neuro-utils';
import { getDiagnosisQueryString, maxDateToNextYearOrMonth } from '../utils';
import { formatRegExpArray, formatStringArray, partialDateToString } from '../utils/queryFormatters';
import { TPlatformHandler } from '.';
import store from 'Store/index';
import { getAtcCodesByPlatform } from 'Routes/Medication/Document/config';

/** This function returns a query string if it can handle the field, otherwise undefined  */
export const handleCommonField: TPlatformHandler = (field, state) => {
  const medicationSettings = store.getState().settings.orgSettings.medicationSettings;

  const diseaseModifyingDrugs =
    typeof state.platform === 'string' &&
    state.platform.length > 0 &&
    medicationSettings &&
    Object.keys(medicationSettings).includes(state.platform)
      ? '$' + formatStringArray(getAtcCodesByPlatform(medicationSettings, state.platform))
      : '$[]';

  const specificMedication = (medication: string[]) => '$' + formatStringArray(medication);

  const specificAtc = (atc: string[]) => formatRegExpArray('atc', atc);

  let query: string | undefined = undefined;
  switch (field) {
    /**
     * The following common fields do not have a dedicated handler (handled in function makeFinalSearchStringArray):
     * - age
     * - gender
     * - includeDeceased
     */

    /**
     * Batch handle:
     * - platform
     * - diagnosis
     * - diagnosisDate
     */
    case 'platform': {
      query = '';
      const dg = state.diagnosis;
      const dgDate = state.diagnosisDate;

      if (state.platform === 'ninmt') {
        query += 'ninmtTreatmentPeriod@date!=NULL';
        break;
      }

      // Start of the diagnosis query string and list of diagnoses
      query += 'diagnosis@_€{data.diagnosis€$';
      query += getDiagnosisQueryString(state.platform, dg);

      if (dgDate !== 'noCropping') {
        // diagnosisDate
        query += '&';
        if (isPartialDate(dgDate.minDiagnosisDate)) {
          query += `data.date>=%${partialDateToString(dgDate.minDiagnosisDate)}`;
          if (isPartialDate(dgDate.maxDiagnosisDate)) query += `&`;
        }
        if (isPartialDate(dgDate.maxDiagnosisDate))
          query += `data.date<=%${partialDateToString(maxDateToNextYearOrMonth(dgDate.maxDiagnosisDate))}`;
      }
      query += '}';
      break;
    }

    case 'medication': {
      query = '';
      if (state.medication !== 'noCropping' && Array.isArray(state.medication) && state.medication.length > 0) {
        // First filter empty medication criteria
        const filteredMedications = state.medication.filter(med => med.medication);
        filteredMedications.forEach((m, i, arr) => {
          // Mgravis duration dependent special case
          if (
            state.platform === 'mgravis' &&
            ['specificMedication'].includes(m.medication!) &&
            ['inUse', 'earlierInUse'].includes(m.status ?? '') &&
            ((m.durationMonths && m.durationMonths !== 'noCropping') ||
              (exists(m.monthsSinceEnd) && m.monthsSinceEnd !== 'noCropping'))
          ) {
            query += `§getDocumentWithDuration[$medication,$startDate,$endDate,%"${partialDateToString(nowPartialDate())}"]€{`;
            if (Array.isArray(m.specificMedication)) {
              // There are medication documents with given medication name(s)
              query += `data.document_data.medicationName€${specificMedication(m.specificMedication)}`;
            } else {
              // There are medication documents with disease modifying drugs
              query += `data.document_data.atc€${diseaseModifyingDrugs}`;
            }
            // status === 'inUse'
            if (m.status === 'inUse') query += '&data.document_data.hasEnded€{exists==FALSE|data.0==FALSE}';
            // status === 'earlierInUse'
            if (m.status === 'earlierInUse') {
              query += '&data.document_data.hasEnded€{exists==TRUE&data.0==TRUE}';
              if (m.monthsSinceEnd && m.monthsSinceEnd !== 'noCropping') {
                query += `&data.document_data.endDate<=%"${partialDateToString(subtractMonths(nowPartialDate(), m.monthsSinceEnd))}"`;
              }
            }
            if (m.durationMonths && m.durationMonths !== 'noCropping') {
              if (m.durationMonths.minDuration) query += `&data.duration_months>=#${m.durationMonths.minDuration}`;
              if (m.durationMonths.maxDuration) query += `&data.duration_months<=#${m.durationMonths.maxDuration}`;
            }
            query += '}';
            return;
          }
          const noMedicationQuery = (): string => {
            let q = '';
            // There are no medication documents
            q += `§countDocsInDateRange[$medication,NULL,%"${partialDateToString(nowPartialDate())}",$startDate]==#0`;
            // OR medication documents do not match criteria
            if (m.status === 'inUse')
              q += `|medication@_€{data.atc!€${diseaseModifyingDrugs}|data.hasEnded€{exists==TRUE&data.0==TRUE}}`;
            if (m.status === 'earlierInUse')
              q += `|medication@_€{data.atc!€${diseaseModifyingDrugs}|data.hasEnded€{exists==FALSE|data.0==FALSE}}`;
            return q;
          };
          query += '(';
          if (['anyMedication', 'specificMedication', 'specificAtc'].includes(m.medication!)) {
            if (m.medication === 'specificMedication' && Array.isArray(m.specificMedication)) {
              // status === 'notInUse'
              if (m.status === 'notInUse') query += '!';
              // There are (no) medication documents with given medication name(s)
              query += `medication@_€{data.medicationName€${specificMedication(m.specificMedication)}`;
            } else if (m.medication === 'specificAtc' && Array.isArray(m.specificAtc)) {
              // There are medication documents with given ATC code(s)
              query += `medication@_€{${specificAtc(m.specificAtc)}`;
            } else {
              if (m.medication === 'anyMedication' || state.platform === 'ms') {
                // There are medication documents with disease modifying drugs
                query += `medication@_€{data.atc€${diseaseModifyingDrugs}`;
              } else if (m.status === 'notInUse') {
                // There are no medication documents
                query += noMedicationQuery();
              } else {
                // There are medication documents
                query += 'medication@_€{data.startDate!=NULL';
              }
            }
            // status === 'notInUse' - handled above
            // status === 'inUse'
            if (m.status === 'inUse') query += '&data.hasEnded€{exists==FALSE|data.0==FALSE}';
            // status === 'earlierInUse'
            if (m.status === 'earlierInUse') query += '&data.hasEnded€{exists==TRUE&data.0==TRUE}';
            query += '}';
          }
          if (m.medication === 'noMedication') {
            query += noMedicationQuery();
          }
          const arrNext = arr[i + 1];
          query += `)${arrNext && arrNext.medication ? '&' : ''}`;
          if (i === arr.length - 1) query = `(${query})`;
        });
      }
      break;
    }

    case 'substance': {
      query = '';
      if (state.substance !== 'noCropping' && Array.isArray(state.substance) && state.substance.length > 0) {
        // First filter empty medication criteria
        const filteredSubstances = state.substance.filter(sub => sub.substance);
        filteredSubstances.forEach((sub, i, arr) => {
          if (!sub.substance) return;
          query += '(';
          // Mgravis duration dependent special case
          if (
            state.platform === 'mgravis' &&
            ['inUse', 'earlierInUse'].includes(sub.status ?? '') &&
            ((sub.durationMonths && sub.durationMonths !== 'noCropping') ||
              (exists(sub.monthsSinceEnd) && sub.monthsSinceEnd !== 'noCropping'))
          ) {
            query += '§getDocumentWithDuration[$medication,$startDate,$endDate]€{';
            // There are substance documents with given string
            query += `data.document_data.medicationSubstances==/"${sub.substance[0]}"`;
            // status === 'inUse'
            if (sub.status === 'inUse') query += '&data.document_data.hasEnded€{exists==FALSE|data.0==FALSE}';
            // status === 'earlierInUse'
            if (sub.status === 'earlierInUse') {
              query += '&data.document_data.hasEnded€{exists==TRUE&data.0==TRUE}';
              if (sub.monthsSinceEnd && sub.monthsSinceEnd !== 'noCropping') {
                query += `&data.months_since_end>=#${sub.monthsSinceEnd}`;
              }
            }
            if (sub.durationMonths && sub.durationMonths !== 'noCropping') {
              if (sub.durationMonths.minDuration) query += `&data.duration_months>=#${sub.durationMonths.minDuration}`;
              if (sub.durationMonths.maxDuration) query += `&data.duration_months<=#${sub.durationMonths.maxDuration}`;
            }
            query += '}';
          } else {
            // status === 'notInUse'
            if (sub.status === 'notInUse') query += '!';
            // There are (no) substance documents with given string
            query += `medication@_€{data.medicationSubstances==/"${sub.substance[0]}"`;

            // status === 'notInUse' - handled above
            // status === 'inUse'
            if (sub.status === 'inUse') query += '&data.hasEnded€{exists==FALSE|data.0==FALSE}';
            // status === 'earlierInUse'
            if (sub.status === 'earlierInUse') query += '&data.hasEnded€{exists==TRUE&data.0==TRUE}';
            query += '}';
          }

          // Ending
          const arrNext = arr[i + 1];
          query += `)${arrNext && arrNext.substance ? '&' : ''}`;
          if (i === arr.length - 1) query = `(${query})`;
        });
      }
      break;
    }

    case 'myServiceInUse': {
      query = '';
      if (Boolean(state.myServiceInUse) && state.myServiceInUse !== 'noCropping') {
        const mysqStatus = state.myServiceInUse === 'yes' ? 'TRUE' : 'FALSE';
        const queryString = `§mysqUserStatus€{data.is_mysq_user==${mysqStatus}${
          mysqStatus === 'TRUE' ? '|' : '&'
        }data.has_delegates==${mysqStatus}}`;
        query = queryString;
      }
      break;
    }

    default:
      // This handler doesn't recognize the field, so it returns undefined.
      return undefined;
  }

  return query;
};
